import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { api } from "../api/endpoints";
import { errorMessage } from "../api/client";
import { useRealtime } from "../realtime/RealtimeProvider";
import { useConfirm } from "../components/Confirm";
import { useToast } from "../components/Toast";
import { prefersReducedMotion } from "../hud/motion";

// The in-app panic control from the approved mockup: a red shield outline
// that fills from the bottom while you hold it (1.2 s), then fires.
// Firing needs a deliberate hold (mouse, touch, or Space/Enter held down).
// Screen-reader activation (a click with no press behind it) opens a confirm
// dialog instead, so it is usable without a hold but never fires by accident.

const HOLD_MS = 1200;
const SHIELD = "M22 2 L41 9 C41 30 34 42 22 48 C10 42 3 30 3 9 Z";

type Phase = "idle" | "holding" | "sending" | "sent" | "failed";

export function PanicShield() {
  const { raiseOwn } = useRealtime();
  const confirm = useConfirm();
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const startAt = useRef<number | null>(null);
  const raf = useRef<number | null>(null);
  const pressSource = useRef<"pointer" | "key" | null>(null);
  /** Time of the last real pointer/key press; clicks right after it are not AT clicks. */
  const lastPressAt = useRef(0);
  const hintId = useId();
  const clipId = `shield-${useId().replace(/:/g, "")}`;

  const cancelFrame = () => {
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    raf.current = null;
  };

  const fire = useCallback(async () => {
    setPhase("sending");
    setProgress(1);
    try {
      await raiseOwn(() => api.raiseAlert({ body: "Raised from the website." }));
      setPhase("sent");
      toast.show("Panic alert sent to everyone at the site.");
    } catch (err) {
      setPhase("failed");
      toast.show(`Alert NOT sent. ${errorMessage(err)}`, "error");
    }
  }, [raiseOwn, toast]);

  const tick = useCallback(() => {
    if (startAt.current === null) return;
    const held = performance.now() - startAt.current;
    let p = Math.min(1, held / HOLD_MS);
    // Reduced motion: discrete quarter steps instead of a sweeping fill.
    if (prefersReducedMotion()) p = Math.floor(p * 4) / 4;
    setProgress(p);
    if (held >= HOLD_MS) {
      startAt.current = null;
      pressSource.current = null;
      raf.current = null;
      void fire();
      return;
    }
    raf.current = requestAnimationFrame(tick);
  }, [fire]);

  const begin = useCallback(
    (source: "pointer" | "key") => {
      if (phase === "sending" || startAt.current !== null) return;
      pressSource.current = source;
      startAt.current = performance.now();
      setPhase("holding");
      setProgress(0);
      cancelFrame();
      raf.current = requestAnimationFrame(tick);
    },
    [phase, tick],
  );

  const abort = useCallback(() => {
    if (startAt.current === null) return;
    startAt.current = null;
    pressSource.current = null;
    cancelFrame();
    setProgress(0);
    setPhase("idle");
  }, []);

  useEffect(() => () => cancelFrame(), []);

  // "Sent" / "failed" fall back to idle after a few seconds.
  useEffect(() => {
    if (phase !== "sent" && phase !== "failed") return;
    const t = setTimeout(() => {
      setPhase("idle");
      setProgress(0);
    }, 4000);
    return () => clearTimeout(t);
  }, [phase]);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    lastPressAt.current = performance.now();
    e.currentTarget.setPointerCapture(e.pointerId);
    begin("pointer");
  };
  const onPointerEnd = () => {
    lastPressAt.current = performance.now();
    if (pressSource.current === "pointer") abort();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    lastPressAt.current = performance.now();
    if (!e.repeat) begin("key");
  };
  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    lastPressAt.current = performance.now();
    if (pressSource.current === "key") abort();
  };

  // A click with no pointer or key hold behind it comes from assistive tech.
  const onClick = async () => {
    if (startAt.current !== null || phase === "sending" || performance.now() - lastPressAt.current < 1500) return;
    const ok = await confirm({
      title: "Send a panic alert?",
      body: "Everyone at the site gets an alarm and a push notification right away.",
      confirmLabel: "Send panic alert",
    });
    if (ok) void fire();
  };

  const fill = phase === "sending" || phase === "sent" ? 1 : progress;
  const label =
    phase === "holding" ? "HOLD" : phase === "sending" ? "SENDING" : phase === "sent" ? "SENT" : phase === "failed" ? "FAILED" : "PANIC";

  return (
    <>
      <button
        type="button"
        className={`panic panic--${phase}`}
        aria-label="Panic: hold for 1.2 seconds to alert everyone at the site"
        aria-describedby={hintId}
        aria-disabled={phase === "sending" ? true : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={abort}
        onClick={onClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg viewBox="0 0 44 50" aria-hidden="true" focusable="false">
          <defs>
            <clipPath id={clipId}>
              <path d={SHIELD} />
            </clipPath>
          </defs>
          <rect className="panic__fill" x="0" y="0" width="44" height="50" clipPath={`url(#${clipId})`} style={{ transform: `scaleY(${fill})` }} />
          <path className="panic__outline" d={SHIELD} />
        </svg>
        <span className="panic__label" aria-hidden="true">
          {label}
        </span>
        <span id={hintId} className="sr-only">
          Press and hold, or hold Space or Enter.
        </span>
      </button>
      <span className="sr-only" aria-live="assertive">
        {phase === "sending" ? "Sending panic alert" : phase === "sent" ? "Panic alert sent" : phase === "failed" ? "Panic alert failed" : ""}
      </span>
    </>
  );
}
